import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../supabaseClient'
import useServices from '../models/useServices'
import useAuth from '../models/useAuth'
import useAccessGrants from '../models/useAccessGrants'

/**
 * Subscribes to INSERT events on access_grants. RLS restricts these to grants
 * for services the current user owns. When a new pending request arrives, opens
 * the Shared access modal filtered to that service.
 */
export default function useAccessRequestNotifier() {
  const navigate = useNavigate()
  const userId = useAuth(state => state.user?.id)
  const services = useServices(state => state.services)
  const servicesRef = useRef(services)
  servicesRef.current = services

  useEffect(() => {
    if (!userId) return

    // Keep global access grants store hydrated and live (used by NodeServices etc.)
    const { get: getGrants, subscribe: subscribeGrants } = useAccessGrants.getState()
    getGrants()
    const unsubscribeGrants = subscribeGrants()

    const channel = supabase
      .channel('access_grants-notifier')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'access_grants' }, payload => {
        const row = payload.new as { service_id: string; granted_at: string | null }
        if (row.granted_at) return
        const service = servicesRef.current.find(s => s.id === row.service_id)
        if (!service) return
        navigate(`/permissions/${service.name}`)
      })
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
      unsubscribeGrants()
    }
  }, [userId])
}
